/**
 * useFocusTrap — accessibility lifecycle for overlays (Dialog, MobileDrawer,
 * CommandBar).
 *
 * Two jobs, both required by WCAG for modal surfaces:
 *
 *   1. Trap Tab focus inside the panel while it's open. Tab past the last
 *      focusable wraps to the first; Shift+Tab past the first wraps to the
 *      last. Without this, Tab escapes to the page behind the scrim — the
 *      P0 we're fixing.
 *
 *   2. Restore focus to whatever element was focused before the overlay
 *      opened, when it closes (the unmount). Without this, a keyboard user
 *      who closes a dialog is dumped at the top of the document.
 *
 * Why a hook and not a wrapper component: the three overlays already own
 * their own portals / mount logic and their own ESC handlers. The hook only
 * needs the panel ref + an `active` flag, so it stays orthogonal to all of
 * that.
 *
 * Coexistence notes (don't break existing behaviour):
 *   - On activate, we only move focus into the panel if focus isn't already
 *     inside it. ConfirmDialog's confirm button and CommandBar's input use
 *     `autoFocus`, which fires on mount before this effect's focus call —
 *     we respect it instead of stealing focus to the panel wrapper.
 *   - We attach the Tab handler to the panel node itself (not document), so
 *     it never competes with the document-level ESC listeners the overlays
 *     already register, and `fireEvent.keyDown(input, …)` in tests bubbles
 *     up to it normally.
 */

import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      el.offsetWidth > 0 ||
      el.offsetHeight > 0 ||
      el.getClientRects().length > 0 ||
      // jsdom/happy-dom report 0 dimensions for everything; don't filter
      // those out in the test env or the trap would treat the panel as
      // empty. The `=== document.activeElement` check keeps the currently
      // focused node eligible regardless of layout.
      el === document.activeElement,
  );
}

/**
 * @param ref     The overlay panel element (the dialog card, not the scrim).
 * @param active  Whether the overlay is currently open. Focus is trapped and
 *                restored only while this is true; flipping it false runs the
 *                restore.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    // Save the trigger so we can hand focus back on close.
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Move focus into the panel — unless an autoFocus child already grabbed
    // it (focus is already inside) or the panel itself is focused.
    if (!node.contains(document.activeElement)) {
      const focusables = getFocusable(node);
      const target = focusables[0] ?? node;
      // The panel wrapper needs to be focusable as a fallback target.
      if (target === node && !node.hasAttribute('tabindex')) {
        node.setAttribute('tabindex', '-1');
      }
      target.focus();
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = getFocusable(node);
      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus on the panel.
        e.preventDefault();
        node.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !node.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !node.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    node.addEventListener('keydown', onKeyDown);

    return () => {
      node.removeEventListener('keydown', onKeyDown);
      // Restore focus to the trigger if it's still in the document.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
